'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@core/utils';
import type { Editor } from '@tiptap/core';
import ListItem from '@tiptap/extension-list-item';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import type { EditorOptions, Extensions } from '@tiptap/react';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import type { ReactNode } from 'react';
import { createContext } from 'react';
import { ImageButton } from './_components/ImageButton';
import { TEXT_ALIGN } from './_constants';
import BoxedText from './_utils/BoxedText';
import { CustomIframe } from './_utils/CustomIframe';
import { CustomImage } from './_utils/CustomImage';
import { CustomLink } from './_utils/CustomLink';
import { CustomVideo } from './_utils/CustomVideo';
import { FontSize } from './_utils/FontSize';
import { FontWeight } from './_utils/FontWeight';
import { Highlight } from './_utils/Highlight';
import { PasteFilter } from './_utils/PasteFilter';
import { TextColor } from './_utils/TextColor';
import { BizToolbar } from './BizToolbar';
import { Contents } from './Contents';
import { Toolbar } from './Toolbar';
export type { ToolbarProps as CustomEditorBizToolbarProps } from './BizToolbar';
export type { CustomEditorContentsProps } from './Contents';
export type { ToolbarProps as CustomEditorToolbarProps } from './Toolbar';
export interface CustomEditorProviderProps extends Partial<Pick<EditorOptions, 'onUpdate' | 'content' | 'onFocus' | 'onBlur' | 'onCreate'>> {
  children?: ReactNode;
  placeholder?: string;
  placeholderClass?: string;
  extraExtensions?: Extensions;
}
interface CustomEditorContextValue {
  editor: Editor | null;
}
export const CustomEditorContext = createContext<CustomEditorContextValue>({
  editor: null
});
const FIXED_EXTENSIONS: Extensions = [FontWeight, Highlight, BoxedText, FontSize, CustomLink, PasteFilter, CustomImage, CustomIframe, CustomVideo, TextColor, TextStyle.configure({
  HTMLAttributes: {
    types: [ListItem.name, StarterKit.name]
  }
}), StarterKit.configure({
  bulletList: {
    keepMarks: true,
    keepAttributes: false
  },
  heading: {
    levels: [2, 3, 4, 5, 6]
  },
  paragraph: {
    HTMLAttributes: {
      class: 'min-h-24 typo-body3 font-400 Gray-black'
    }
  },
  horizontalRule: {
    HTMLAttributes: {
      class: 'border-t-1 border-Gray-300 my-30'
    }
  },
  orderedList: {
    keepMarks: true,
    keepAttributes: true
  }
})];
const Provider = t0 => {
  const $ = _c(23);
  let children;
  let editorProps;
  let placeholderClass;
  let t1;
  let t2;
  if ($[0] !== t0) {
    ({
      children,
      placeholder: t1,
      placeholderClass,
      extraExtensions: t2,
      ...editorProps
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = editorProps;
    $[3] = placeholderClass;
    $[4] = t1;
    $[5] = t2;
  } else {
    children = $[1];
    editorProps = $[2];
    placeholderClass = $[3];
    t1 = $[4];
    t2 = $[5];
  }
  const placeholder = t1 === undefined ? "" : t1;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[6] !== editorProps || $[7] !== placeholder || $[8] !== placeholderClass || $[9] !== t2) {
    const extraExtensions = t2 === undefined ? [] : t2;
    t6 = useEditor;
    t3 = editorProps;
    t4 = false;
    t5 = FIXED_EXTENSIONS.concat([...extraExtensions, TextAlign.configure({
      types: ["heading", "paragraph"],
      alignments: TEXT_ALIGN
    }), Placeholder.configure({
      placeholder,
      emptyNodeClass: cn("first:before:pointer-events-none first:before:float-left first:before:text-Gray-500 first:before:content-[attr(data-placeholder)]", placeholderClass)
    })]);
    $[6] = editorProps;
    $[7] = placeholder;
    $[8] = placeholderClass;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t3 || $[15] !== t4 || $[16] !== t5) {
    t7 = {
      ...t3,
      immediatelyRender: t4,
      extensions: t5
    };
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const editor = t6(t7);
  let t8;
  if ($[18] !== editor) {
    t8 = {
      editor
    };
    $[18] = editor;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== children || $[21] !== t8) {
    t9 = <CustomEditorContext.Provider value={t8}>{children}</CustomEditorContext.Provider>;
    $[20] = children;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
};
export const CustomEditor = {
  Provider,
  Toolbar,
  BizToolbar,
  Contents,
  ImageButton
};